import React, { useMemo } from 'react'

import moment from 'moment'
import get from 'lodash/get'
import padStart from 'lodash/padStart'
import { useSelector } from 'react-redux'
import { getIn, useFormikContext } from 'formik'
import { NumberInput, ColorLabel, Tooltip, Button } from 'components/atoms'
import { selectAssetCodeAutoGenerateSettingListData, selectTermYearSelected } from 'store/settings/assetCode/selectors'
import { ASSET_CODE_FORMAT_RULE, LIST_ASSET_CODE_FORMAT } from './const'
import { Table, Row, SubmitWrapper } from './styles'
import { selectTermsData } from 'store/settings/accountingPeriod/selectors'
import { checkTermBelongToTermSpan } from 'utils/MonthlyClosingHelper'
import { loadNs } from 'aa_common/front-end/i18n-config'

const t = loadNs(['common', 'components/organisms/settings/asset-code-start-number-page'])

type Props = {
  lengthRuleNumber: number
}

type Rule = {
  field: number
  value: number | string
}

const Form: React.FC<Props> = ({ lengthRuleNumber = 3 }) => {
  const { values } = useFormikContext()
  const codeAutoGenerateSettingList = useSelector(selectAssetCodeAutoGenerateSettingListData)
  const termYearSelected = useSelector(selectTermYearSelected)
  const terms = useSelector(selectTermsData)
  const assetCategoryInitialValues = getIn(values, 'asset_category_initial_values')
  const rules = useMemo(() => {
    const term = terms.find(term => term.id === termYearSelected)
    return codeAutoGenerateSettingList.find(setting => checkTermBelongToTermSpan(setting, term))?.rules
  }, [terms, codeAutoGenerateSettingList, termYearSelected])

  const isIncludeAcquisitionDate = rules?.some(rule => rule.field === ASSET_CODE_FORMAT_RULE.RULE_2)

  const renderAssetCodePreview = (assetCategory: any) => {
    const suffix = padStart(get(assetCategory, 'initial_value').toString(), lengthRuleNumber, '0') + '-001'

    const cases = {
      [ASSET_CODE_FORMAT_RULE.RULE_1]: `${assetCategory.asset_category_code}`,
      [ASSET_CODE_FORMAT_RULE.RULE_2]: (rule: Rule) => {
        const formatType = LIST_ASSET_CODE_FORMAT[rule.field][`${rule.value}`].format
        return `${moment().format(formatType)}`
      },
      [ASSET_CODE_FORMAT_RULE.RULE_3]: (rule: Rule) => LIST_ASSET_CODE_FORMAT[rule.field][`${rule.value}`].value,
      [ASSET_CODE_FORMAT_RULE.RULE_4]: (rule: Rule) => rule.value,
    } as any

    const result = rules?.map(rule => {
      const eachCase = cases[rule.field]
      return (typeof eachCase === 'function' ? eachCase(rule) : eachCase) || ''
    })

    return result?.join('') + suffix
  }

  return (
    <>
      {assetCategoryInitialValues && (
        <Table>
          {assetCategoryInitialValues.map((assetCategory: any, index: number) => {
            const assetCodePreview = renderAssetCodePreview(assetCategory)

            return (
              <Row key={assetCategory.id} className={index === assetCategoryInitialValues.length - 1 ? 'last' : ''}>
                <div className="label">{assetCategory.asset_category_name}</div>
                <div className="content">
                  {assetCategory.is_editable ? (
                    <NumberInput
                      name={`asset_category_initial_values[${index}].initial_value`}
                      style={{ width: 100 }}
                      maxLength={lengthRuleNumber}
                    />
                  ) : (
                    <Tooltip content={t('disabled_tooltip_msg')} placement="top">
                      <NumberInput
                        name={`asset_category_initial_values[${index}].initial_value`}
                        style={{ width: 100 }}
                        disabled
                      />
                    </Tooltip>
                  )}

                  <ColorLabel color="disabled" style={{ marginLeft: 10, marginTop: 5 }}>
                    {t('code_sample', { code: assetCodePreview })}
                    {isIncludeAcquisitionDate && t('incase_registering_today')}
                  </ColorLabel>
                </div>
              </Row>
            )
          })}
        </Table>
      )}
      <SubmitWrapper>
        <Button type="submit" color="blue" size="custom" style={{ width: 70 }}>
          {t('actions.save')}
        </Button>
      </SubmitWrapper>
    </>
  )
}

export default Form
